import React from "react";
import { twMerge } from "tailwind-merge";
import { type ComponentFormatter, formatComponentValue } from "../../utils/formatting";
import { truncateMiddle } from "../../utils/formatting/text";
import type { FieldValue } from "../../utils/formatting/types";
import { CopyToClipboard } from "../CopyToClipboard";
import { Icon } from "../Icon";
import { Skeleton } from "../Skeleton";
import { Tooltip } from "../Tooltip";

/**
 * StatList — Compact key-value statistics display
 *
 * Displays formatted statistics with status indicators and optional copy functionality.
 * Uses the unified formatting system for consistent data presentation.
 */

// Types

export type StatTone = "neutral" | "success" | "warning" | "error" | "info";
export type StatAlign = "start" | "end";
export type StatLayout = "one-column" | "two-column";

export type StatValue = FieldValue | React.ReactElement;

export type StatFormatter = ComponentFormatter;

export interface StatThreshold {
  when: (value: StatValue) => boolean;
  tone: StatTone;
}

export interface StatItem {
  /** Unique identifier */
  id: string;
  /** Label for the statistic */
  label: React.ReactNode;
  /** Value to display */
  value: StatValue;
  /** Custom formatter for the value */
  formatter?: StatFormatter;
  /** Additional metadata below value */
  meta?: React.ReactNode;
  /** Explicit tone/status color */
  tone?: StatTone;
  /** Data-driven tone based on value */
  thresholds?: StatThreshold[];
  /** Enable copy to clipboard */
  copyable?: boolean | ((value: StatValue) => string);
  /** Position of the copy icon when copyable is enabled, defaults to "left" */
  copyIconPosition?: "left" | "right";
  /** Optional link URL */
  href?: string;
  /** Click handler */
  onAction?: () => void;
  /** Icon before label */
  iconLeft?: React.ReactNode;
  /** Icon after value */
  iconRight?: React.ReactNode;
  /** Tooltip for additional info */
  tooltip?: React.ReactNode;
  /** Disabled state */
  disabled?: boolean;
  /** Stack label above value on mobile screens (useful for multi-line values like tags) */
  stackOnMobile?: boolean;

  /**
   * PII type for masking (e.g., "email", "phone", "full-name")
   * When provided along with piiEntity, adds data-pii-* attributes for masking
   */
  piiType?: string;

  /**
   * Entity identifier for PII masking (e.g., "contact:ctc_abc123")
   */
  piiEntity?: string;
}

export interface StatListProps {
  /** Statistics to display */
  items: StatItem[];
  /** Layout mode */
  layout?: StatLayout;
  /** Compact spacing */
  dense?: boolean;
  /** Value alignment */
  valueAlign?: StatAlign;
  /** Show dividers between items */
  showDividers?: boolean;
  /** Loading state */
  skeleton?: boolean;
  /** Empty state */
  empty?: boolean;
  /** Error state */
  error?: string | React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

// Helpers

// Use the centralized formatting utility
function formatValue(value: StatValue, formatter?: StatFormatter): React.ReactNode {
  // If value is already a React element, return it as-is (skip formatting)
  if (React.isValidElement(value)) {
    return value;
  }

  return formatComponentValue({
    value,
    formatter,
    emptyClassName: "text-text-secondary",
    emptyText: "—",
  });
}

function getTone(item: StatItem): StatTone | undefined {
  // Check thresholds first
  if (item.thresholds && item.value !== null && item.value !== undefined) {
    for (const threshold of item.thresholds) {
      if (threshold.when(item.value)) {
        return threshold.tone;
      }
    }
  }

  // Fall back to explicit tone
  return item.tone;
}

const toneColors: Record<StatTone, string> = {
  neutral: "text-text-primary",
  success: "text-feedback-success",
  warning: "text-feedback-warning",
  error: "text-feedback-error",
  info: "text-feedback-info",
};

// StatRow Component

// Constants for auto-truncation
const LONG_STRING_THRESHOLD = 24;
const LONG_STRING_TRUNCATE_LENGTH = 20;

function StatRow({
  item,
  dense,
  valueAlign,
  divider,
}: {
  item: StatItem;
  dense?: boolean;
  valueAlign?: StatAlign;
  /** Rule classes below this row. Owned by the row so each COLUMN rules independently. */
  divider?: string;
}) {
  const tone = getTone(item);
  const toneClass = tone ? toneColors[tone] : "";
  const isStacked = item.stackOnMobile;

  // Check if this is a long string that should be auto-truncated on mobile
  const isLongString =
    typeof item.value === "string" &&
    item.value.length > LONG_STRING_THRESHOLD &&
    !item.formatter &&
    !React.isValidElement(item.value);

  // Get the formatted value (used for both truncated and full display)
  const formattedValue = formatValue(item.value, item.formatter);

  // Get the truncated value for mobile display
  // Note: We cast to string here because isLongString already verifies typeof item.value === "string"
  const truncatedValue = isLongString ? truncateMiddle(item.value as string, LONG_STRING_TRUNCATE_LENGTH) : null;

  const textToCopy = typeof item.copyable === "function" ? item.copyable(item.value) : String(item.value);

  // PII data attributes (only set if both piiType and piiEntity are provided)
  const piiAttrs =
    item.piiType && item.piiEntity
      ? {
          "data-pii-type": item.piiType,
          "data-pii-entity": item.piiEntity,
        }
      : {};

  // For long strings, render both truncated (mobile) and full (desktop) versions
  // CSS classes control which is visible based on screen size
  // Tooltip content is wrapped in span with PII attributes to allow masking by Curtain extension
  const hasPiiAttrs = Object.keys(piiAttrs).length > 0;
  const valueDisplay = isLongString ? (
    <>
      {/* Mobile: show truncated with tooltip */}
      <Tooltip content={hasPiiAttrs ? <span {...piiAttrs}>{String(item.value)}</span> : String(item.value)}>
        <span className={twMerge("@md:hidden", item.href ? "hover:underline cursor-pointer" : "")} {...piiAttrs}>
          {truncatedValue}
        </span>
      </Tooltip>
      {/* Desktop: show full value */}
      <span className={twMerge("hidden @md:inline", item.href ? "hover:underline cursor-pointer" : "")} {...piiAttrs}>
        {formattedValue}
      </span>
    </>
  ) : (
    <span className={item.href ? "hover:underline cursor-pointer" : ""} {...piiAttrs}>
      {formattedValue}
    </span>
  );

  // Copy button component
  const copyButton = item.copyable ? (
    <CopyToClipboard value={textToCopy} size="sm">
      <span className="sr-only">Copy</span>
    </CopyToClipboard>
  ) : null;

  const hasCopyLeft = item.copyable && (item.copyIconPosition === "left" || !item.copyIconPosition);
  const hasCopyRight = item.copyable && item.copyIconPosition === "right";

  // When stacked on mobile, align value to left on mobile, restore alignment on sm+
  const valueAlignClass = isStacked
    ? valueAlign === "start"
      ? "text-left"
      : "text-left @sm:text-right"
    : valueAlign === "start"
      ? "text-left"
      : "text-right";

  const valueContent = (
    <dd
      className={twMerge(
        dense ? "text-sm" : "text-base",
        "font-medium",
        valueAlignClass,
        toneClass,
        item.disabled && "opacity-50",
        "overflow-hidden min-w-0"
      )}
    >
      <div
        className={twMerge(
          "inline-flex items-center gap-1 max-w-full",
          valueAlign === "start" ? "justify-start" : "justify-end"
        )}
      >
        <span className="inline-flex items-center gap-1 min-w-0">
          {valueDisplay}
          {item.iconRight}
        </span>
        {hasCopyRight && copyButton}
      </div>

      {item.meta && (
        <div
          className={twMerge("text-xs text-text-secondary mt-0.5", valueAlign === "start" ? "text-left" : "text-right")}
        >
          {item.meta}
        </div>
      )}
    </dd>
  );

  const content = (
    <>
      <dt
        className={twMerge(
          dense ? "text-xs" : "text-sm",
          "text-text-secondary truncate",
          "flex items-center gap-1",
          item.disabled && "opacity-50"
        )}
      >
        {item.iconLeft}
        <span title={typeof item.label === "string" ? item.label : undefined}>{item.label}</span>
        {item.tooltip && (
          <Icon name="Question" size={10} className="text-text-secondary" aria-label="More information" />
        )}
      </dt>

      {/*
        The copy button is a `CopyToClipboard`, which hard-codes `!px-2` for its
        click target — 8px of padding on each side of a 14px glyph. On the left
        of a value that trailing 8px reads as a GAP rather than as part of the
        button, and with the grid's own `gap-x-1` the icon ended up 12px from the
        value it copies, far enough to look unattached to it.

        So the trailing padding is pulled back visually while the button keeps
        its full width as a target: the negative margin closes the gap to 4px,
        which matches the grid gutter used everywhere else in the row.
      */}
      {/*
        `CopyToClipboard` hard-codes `!px-2` for its click target and nudges
        itself down half a pixel, so the button is a 14px glyph in a 33px box
        against a 20px value. Left alone it is the tallest thing in its row: a
        copyable row stands ~12px taller than its neighbours and its text sits
        lower, so in a two-column list the columns stop sharing a row pitch.

        `h-5` (20px) on the wrapper with `[&_button]:h-5 [&_button]:py-0` caps
        the height to the text line it sits on; the glyph keeps its full-width
        click target. `-mr-2` pulls back the button's own trailing padding,
        which otherwise reads as a gap between the icon and the value it copies.
      */}
      {hasCopyLeft && (
        <div className="flex h-5 items-center -mr-2 [&_button]:h-5 [&_button]:py-0 [&_button]:top-0">{copyButton}</div>
      )}

      {item.href ? (
        <a href={item.href} onClick={item.onAction} className="block">
          {valueContent}
        </a>
      ) : (
        valueContent
      )}
    </>
  );

  // Grid layout classes - when stacked on mobile, use single column on mobile, restore on sm+
  // Use minmax for label column to prevent collapse when values are wide.
  //
  // A copyable row puts the copy button in a column between the label and the
  // value. That column and the value's are both content-sized, so the LABEL
  // column has to be the one that absorbs the row's slack — with `1fr` on the
  // label and `auto` on the rest, the button and value were pushed hard left,
  // leaving the button sitting against the label instead of the value it
  // copies (~130px adrift in a wide two-column list). Giving the slack to the
  // label keeps the button tucked immediately before its value and the pair
  // flush to the row's end, matching the non-copyable rows above and below.
  // Container variants (`@sm:`), not viewport ones. A list is sized by its
  // CONTAINER: a narrow list on a wide screen used to get the wide answer, so a
  // 230px panel stayed two columns, each value track starved to 0px against the
  // label's 120px floor, and `overflow: hidden` clipped every value to nothing.
  const baseGridCols = hasCopyLeft
    ? "grid-cols-[1fr_auto_auto] gap-x-1"
    : dense
      ? "grid-cols-[minmax(120px,1fr)_1fr] gap-x-2"
      : "grid-cols-[minmax(120px,1fr)_1fr] gap-x-4";

  const stackedGridCols = hasCopyLeft
    ? "grid-cols-1 gap-y-1 @sm:grid-cols-[1fr_auto_auto] @sm:gap-x-1 @sm:gap-y-0"
    : dense
      ? "grid-cols-1 gap-y-1 @sm:grid-cols-[minmax(120px,1fr)_1fr] @sm:gap-x-2 @sm:gap-y-0"
      : "grid-cols-1 gap-y-1 @sm:grid-cols-[minmax(120px,1fr)_1fr] @sm:gap-x-4 @sm:gap-y-0";

  // Centre label, copy button and value on one line.
  //
  // The label is a `text-xs`/`text-sm` line and the value a `text-sm`/`text-base`
  // one, so they are never the same height (16px vs 20px at the dense sizes).
  // Top-aligning them therefore does NOT put them on a shared line — it pins two
  // different-height boxes to the same top edge, and the label reads ~2px high
  // against its own value. In a two-column list that is visible as the right
  // column's labels sitting above the left column's values.
  //
  // Copyable rows already centred (that is what made them look different from
  // their neighbours); every row now does. A stacked row keeps `items-start`,
  // where label and value are on separate lines and centring has nothing to do.
  const alignmentClass = isStacked ? "items-start @sm:items-center" : "items-center";

  return (
    <div
      className={twMerge(
        "grid",
        alignmentClass,
        isStacked ? stackedGridCols : baseGridCols,
        // The rule hangs off the row itself rather than a separate full-width
        // element, so in a two-column layout each column rules its own rows.
        divider,
        // Padding rides the SAME breakpoint as the rule it spaces. A two-column
        // row's divider is `@md:`-only, so unconditional padding would leave a
        // gap under rows that draw no line at narrow width.
        divider && dividerPadding(divider, dense)
      )}
    >
      {content}
    </div>
  );
}

/**
 * Divider classes for an item, or `undefined` for no rule.
 *
 * A trailing rule under the final row reads as a container edge rather than a
 * separator, so the last visual row never rules.
 *
 * A two-column list flows row-major, so with an even count the last TWO items
 * share the final row once the grid splits at `@md:` — but below it the list is
 * one column and the second-to-last item is mid-list. That row is ruled only
 * below `@md:`, the same container breakpoint the grid itself uses, so the two
 * can never disagree.
 */
/**
 * Padding under a divider, at the same width the divider itself applies.
 *
 * A one-column row rules at every width, so its padding is unconditional. A
 * two-column row rules only once the grid splits, so padding it before then
 * would space a line that is not being drawn.
 *
 * Every class is written out in full. Tailwind scans source for complete class
 * names, so a composed string (`\`@md:${pad}\``) generates no CSS at all — the
 * class lands on the element and silently does nothing.
 */
function dividerPadding(divider: string, dense?: boolean): string {
  const splitOnly = divider.includes("@md:");
  if (dense) return splitOnly ? "@md:pb-1" : "pb-1";
  return splitOnly ? "@md:pb-2" : "pb-2";
}

function dividerClass(index: number, count: number, layout: StatLayout): string | undefined {
  if (index === count - 1) return undefined;

  /*
   * A two-column list rules only once it has actually split.
   *
   * The rule earns its keep across a wide row, where a label on the left and a
   * value on the right are far enough apart to lose track of each other — and
   * in two columns it also separates the two columns' rows, which otherwise
   * interleave. Stacked into one narrow column none of that applies: the label
   * and value are already close, and a rule under every row is a line per fact
   * for no reading gained.
   *
   * A one-column list is what its author asked for at every width, so it rules
   * throughout — this is about the layout the container forced, not the one
   * that was chosen.
   */
  if (layout !== "two-column") return "border-b border-border-default";

  // The penultimate item of an even list shares the final visual row once the
  // grid splits, so it never rules: below `@md:` nothing rules, above it that
  // row is the last one.
  if (count % 2 === 0 && index === count - 2) return undefined;

  return "@md:border-b @md:border-border-default";
}

// Main Component

export function StatList({
  items,
  layout = "one-column",
  dense = false,
  valueAlign = "end",
  showDividers = false,
  skeleton = false,
  empty = false,
  error,
  className,
}: StatListProps) {
  // Loading state
  if (skeleton) {
    return (
      // `@container` sits on a WRAPPER: an element cannot match a container
      // query against a container it establishes itself, so the grid below
      // would never see its own width.
      <div className="@container">
        <div
          className={twMerge(
            "grid",
            layout === "two-column" ? "@md:grid-cols-2 gap-x-6" : "grid-cols-1",
            dense ? "gap-y-1" : "gap-y-2",
            className
          )}
        >
          {Array.from({ length: items.length || 4 }).map((_, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto] items-start gap-x-4">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-5 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error or empty states
  if (error) {
    return <div className={twMerge("text-sm text-feedback-error", className)}>{error}</div>;
  }

  if (empty || !items.length) {
    return <div className={twMerge("text-sm text-text-secondary", className)}>No data available</div>;
  }

  return (
    // `@container` sits on a WRAPPER: an element cannot match a container query
    // against a container it establishes itself, so the list would never see
    // its own width.
    <div className="@container">
      <dl
        className={twMerge(
          "grid",
          layout === "two-column" ? "@md:grid-cols-2 gap-x-6" : "grid-cols-1",
          dense ? "gap-y-1" : "gap-y-2",
          className
        )}
      >
        {items.map((item, index) => (
          <StatRow
            key={item.id}
            item={item}
            dense={dense}
            valueAlign={valueAlign}
            divider={showDividers ? dividerClass(index, items.length, layout) : undefined}
          />
        ))}
      </dl>
    </div>
  );
}
